import { ConfigProvider, Button, Descriptions, FloatButton, Input, Select, Tooltip } from 'antd';
import { CloseOutlined, EditOutlined, RetweetOutlined, SaveOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useSelected } from '../../providers/selectedContext';
import { useTheme } from '../../providers/theme';
import { getColors } from '../utils/getColors';
import './specialCore.css';
import { KrTypes, useAppContext } from '../../providers/AppContext';
import WaterOilPanel from './components/waterOilPanel/WaterOilPanel';
import GasOilPanel from './components/gasOilPanel/GasOilPanel';
import { updateSample } from '../../api/data';
import { UpdateSamplePayload } from '../../@types/samples/types';
import SpecialCoreDescriptions from './components/descriptions';
import { Accordion } from '../../design-system/accordion';
import { dropdownStyle } from '../utils/getColors';

export const verifyValue = (value: any) => {
  return value || value === 0 ? value : '--';
};

interface GeneralValues {
  Depth: string;
  'Core Sample Length': string;
  Porosity: string;
  Permeability: string;
  'Oil Permeability': string;
}

const SpecialCore = ({ activeTab }: { activeTab?: string }) => {
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { configStates, updateConfigStates, data, updateData } = useAppContext();

  const changeKrType = () => {
    updateConfigStates({
      krType:
        configStates.krType === KrTypes.WATER_OIL
          ? KrTypes.GAS_OIL
          : KrTypes.WATER_OIL
    });
  };
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const [depthUnit, setDepthUnit] = useState<'m' | 'ft'>('ft');
  const [originalDepthUnit, setOriginalDepthUnit] = useState<'m' | 'ft'>('ft');
  const [editGeneral, setEditGeneral] = useState(false);
  const [generalValues, setGeneralValues] = useState<GeneralValues>({
    Depth: '0',
    'Core Sample Length': '',
    Porosity: '0',
    Permeability: '0',
    'Oil Permeability': '0',
  });
  // snapshot para restore no cancel
  const [originalValues, setOriginalValues] = useState<GeneralValues>(generalValues);

  const [isPanelOpen, setIsPanelOpen] = useState(true);

  // carrega valores iniciais sempre que muda a sample
  useEffect(() => {
    const s = selectedInfo.sample;
    if (!s) return;
    const loaded: GeneralValues = {
      Depth: String(s.depth),
      'Core Sample Length': String(s.sample_length ?? ''),
      Porosity: String(s.input_sample_values.porosity ?? 0),
      Permeability: String(s.input_sample_values.permeability ?? 0),
      'Oil Permeability': String(s.input_sample_values.ko ?? 0),
    };
    setGeneralValues(loaded);
    setOriginalValues(loaded);

    const du: 'm' | 'ft' = s.depth_unit ?? 'ft';
    setDepthUnit(du);
    setOriginalDepthUnit(du);

    setEditGeneral(false);
  }, [selectedInfo.sample]);

  const handleSave = async () => {
    const s = selectedInfo.sample;
    if (!s?.uid) return;

    // helper de conversão
    const toNumOrNull = (str: string): number | null => {
      const n = parseFloat(str.replace(',', '.'));
      return isNaN(n) ? null : n;
    };

    // 1) preenche o payload corretamente
    const payload: UpdateSamplePayload = {
      sample_uid: s.uid,
      depth: toNumOrNull(generalValues.Depth),
      sample_length: toNumOrNull(generalValues['Core Sample Length']),
      depth_unit: depthUnit,
      input_sample_values: {
        porosity: toNumOrNull(generalValues.Porosity),
        permeability: toNumOrNull(generalValues.Permeability),
        ko: toNumOrNull(generalValues['Oil Permeability']),
      }
    };

    try {
      const response = await updateSample(payload);

      // 2) atualiza a lista global
      const updatedList = data.wellSamples.map(w =>
        w.uid === response.uid ? response : w
      );
      updateData({ wellSamples: updatedList });

      // 3) atualiza o contexto passando diretamente o novo objeto
      setSelectedInfo({
        ...selectedInfo,
        sample: response
      });

      // 4) atualiza imediatamente o formulário local
      const updatedValues: GeneralValues = {
        Depth: String(response.depth ?? ''),
        'Core Sample Length': String(response.sample_length ?? ''),
        Porosity: String(response.input_sample_values.porosity ?? ''),
        Permeability: String(response.input_sample_values.permeability ?? ''),
        'Oil Permeability': String(response.input_sample_values.ko ?? ''),
      };
      setGeneralValues(updatedValues);
      setOriginalValues(updatedValues);

      // 5) sai do modo edição
      setEditGeneral(false);
    } catch (error) {
      console.error('Error updating sample:', error);
    }
  };

  const GeneralData = [
    { label: 'Depth', key: 'Depth', suffix: 'm' },
    { label: 'Core Sample Length', key: 'Core Sample Length', suffix: '' },
    { label: 'Porosity', key: 'Porosity', suffix: 'fraction' },
    { label: 'Permeability', key: 'Permeability', suffix: 'mD' },
    { label: 'Oil Permeability', key: 'Oil Permeability', suffix: 'mD' },
  ];

  const handleIconClick = (e, action) => {
    e.stopPropagation();
    action();
  };

  const handlePanelToggle = (open: boolean) => {
    if (editGeneral && !open) {
      setGeneralValues(originalValues);
      setDepthUnit(originalDepthUnit);
      setEditGeneral(false);
    }
    setIsPanelOpen(open);
  };

  const editIcons = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
      {editGeneral ? (
        <>
          <Tooltip title="Save">
            <SaveOutlined
              onClick={(e) => handleIconClick(e, handleSave)}
              style={{ color: colors.colorPrimary, cursor: 'pointer', fontSize: '16px' }}
            />
          </Tooltip>
          <Tooltip title="Cancel">
            <CloseOutlined
              onClick={(e) => handleIconClick(e, () => {
                setGeneralValues(originalValues);
                setDepthUnit(originalDepthUnit);
                setEditGeneral(false);
              })}
              style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: '16px' }}
            />
          </Tooltip>
        </>
      ) : (
        <Tooltip title={isPanelOpen ? "Edit" : "Abra o painel para editar"}>
          <EditOutlined
            onClick={(e) => handleIconClick(e, () => {
              if (!isPanelOpen) return;
              setOriginalValues(generalValues);
              setOriginalDepthUnit(depthUnit);
              setEditGeneral(true);
            })}
            style={{
              color: isPanelOpen ? colors.text : '#999',
              cursor: isPanelOpen ? 'pointer' : 'not-allowed',
              fontSize: '16px'
            }}
          />
        </Tooltip>
      )}
    </div>
  );

  return (
    <div style={{ padding: '1rem' }}>
      <ConfigProvider
        theme={{
          components: {
            Descriptions: {
              colorBgBase: colors.bg,
              contentColor: colors.text,
              labelBg: isDay ? '#fafafa' : '#090909',
              colorBorder: colors.border,
              extraColor: colors.border
            },
            Input: {
              colorBgContainer: isDay ? 'white' : '#141414'
            },
            Table: {
              headerBg: isDay ? '#fafafa' : '#1f1f1f',
              borderColor: isDay ? '#f0f0f0' : '#434343',
              rowHoverBg: isDay ? '#f5f5f5' : '#1f1f1f',
              stickyScrollBarBg: isDay ? 'rgba(0, 0, 0, 0.25)' : '#434343',
              colorBgContainer: colors.bg,
              colorBgBase: colors.bg
            },
            Modal: {
              contentBg: colors.bg,
              headerBg: colors.bg
            }
          },
          token: {
            colorText: colors.text,
            colorBorder: colors.border,
            colorPrimaryHover: colors.colorPrimary,
            colorPrimary: colors.colorPrimary
          }
        }}

      >
        {/* Tab: Sample Information (or no tab — show all) */}
        {(!activeTab || activeTab === 'sample-information') && (
          <>
            <div>
              <SpecialCoreDescriptions
                data={[
                  { label: 'Sample ID', value: verifyValue(selectedInfo.sample?.sample_code) },
                  { label: 'Block/Field', value: verifyValue(selectedInfo.project?.block) },
                  { label: 'Reservoir', value: verifyValue(selectedInfo.project?.reservoir) },
                  { label: 'Well', value: verifyValue(selectedInfo.well?.well_name) },
                  { label: 'Rock Type', value: verifyValue(selectedInfo.project?.rock_type) },
                  { label: 'Lab System', value: verifyValue(selectedInfo.sample?.lab_system) },
                  { label: 'Test Type', value: '--' },
                  { label: 'Method', value: '--' },
                  { label: 'Fluids', value: '--' },
                  { label: 'Analysis Date', value: '--' }
                ]}
                title="Kr Analysis"
              />
            </div>

            <div style={{ marginTop: '1rem' }}>
              <Accordion defaultOpen="general-data">
                <Accordion.Item
                  id="general-data"
                  header={
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 'bold' }}>
                      <span>General Data</span>
                      {editIcons}
                    </div>
                  }
                  onToggle={handlePanelToggle}
                >
                <Descriptions
                  layout="vertical"
                  column={4}
                  className={
                    isDay
                      ? 'special-core-descriptions-day'
                      : 'special-core-descriptions-night'
                  }
                >
                  {GeneralData.map(({ label, key, suffix }) => {
                    const isDepth = key === 'Depth';

                    return (
                      <Descriptions.Item
                        key={key}
                        label={
                          <span
                            style={{
                              color: isDay ? colors.text : '#FAFBFF',
                              paddingTop: '0.5rem',
                              paddingLeft: '0.5rem'
                            }}
                          >
                            {label}
                          </span>
                        }
                        contentStyle={{
                          fontWeight: 'bold',
                          padding: '0.2rem 0.5rem 0.5rem'
                        }}
                      >
                        {editGeneral ? (
                          isDepth ? (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <Input
                                value={generalValues.Depth}
                                onChange={e =>
                                  setGeneralValues(prev => ({ ...prev, Depth: e.target.value }))
                                }
                                style={{ width: 100 }}
                                size="small"
                              />
                              <Select<'m' | 'ft'>
                                value={depthUnit}
                                onChange={val => setDepthUnit(val)}
                                size="small"
                                options={[
                                  { value: 'm', label: 'm' },
                                  { value: 'ft', label: 'ft' }
                                ]}
                                dropdownStyle={dropdownStyle(isDay)}
                              />
                            </div>
                          ) : (
                            <Input
                              value={generalValues[key]}
                              suffix={` ${suffix}`}
                              style={{ width: 120 }}
                              size="small"
                              onChange={e =>
                                setGeneralValues(prev => ({
                                  ...prev,
                                  [key]: e.target.value
                                }))
                              }
                            />
                          )
                        ) : (
                          <>
                            {verifyValue(generalValues[key])}{' '}
                            <span style={{ marginLeft: 4, color: colors.text }}>
                              {isDepth ? depthUnit : suffix}
                            </span>
                          </>
                        )}
                      </Descriptions.Item>
                    );
                  })}
                </Descriptions>
                </Accordion.Item>
              </Accordion>
            </div>
          </>
        )}

        {/* All other tabs delegate to the active analysis panel */}
        {(!activeTab || activeTab !== 'sample-information') && (
          <div style={{ marginTop: '1rem' }}>
            {configStates.krType === KrTypes.WATER_OIL
              ? <WaterOilPanel activeTab={activeTab} />
              : <GasOilPanel activeTab={activeTab} />}
          </div>
        )}

        {/* Switch Button */}
        <FloatButton
          tooltip={
            configStates.krType === KrTypes.WATER_OIL
              ? 'Switch to Gas-Oil'
              : 'Switch to Water-Oil'
          }
          icon={<RetweetOutlined />}
          shape="square"
          type="primary"
          onClick={changeKrType}
        />
      </ConfigProvider>
    </div>
  );
};

export default SpecialCore;
